import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from "@angular/router";
import { forkJoin, Observable, of } from "rxjs";
import { map, mergeMap } from "rxjs/operators";
import {
  dashboardNavLabelForUrl,
  markDashboardSelection,
} from "@partner/feature-modules/dashboard/shared/dashboard-nav.helper";
import { addDefaultAppSidenavItemOptions } from "@partner/shared/Tools";
import { AppPageOptions, AppSidenavComponentData, AppSidenavItem, AppSidenavSubItem } from "common";
import { baseAppSidenavItems } from "./app.data";
import {
  BrokerUserData,
  UserDbRole,
} from "./broker/domain/models/broker.model";
import {
  BrokerCurrentResolver,
  BrokerDataDetailsResolver,
} from "./broker/infrastructure/broker.resolver";
import {
  AdminSidenavFilters,
  TeamSidenavFilters,
  userRoleUiMap,
  WebPaths,
} from "./shared/SharedConstants";

@Injectable({
  providedIn: "root",
})
export class AppPageResolver implements Resolve<AppPageOptions> {
  constructor(
    private brokerUserResolver: BrokerCurrentResolver,
    private brokerDataDetailsResolver: BrokerDataDetailsResolver,
  ) {}

  resolve(): Observable<AppPageOptions> {
    return forkJoin([
      this.brokerUserResolver.resolve(),
      this.brokerDataDetailsResolver.resolve(),
    ]).pipe(
      map(([brokerUser, brokerDataDetails]) => ({
        contentWithoutPadding: true,
        contentClass: "fill default-app-page-content",
        contentAlignment: "start-start",
        sidenavEnabled: true,
        sidenavHeaderSticky: true,
        hideSidenavToggle: true,
        sidenavTitle: brokerUser.firstName ? `${brokerUser.firstName} ${brokerUser.lastName}` : brokerUser.lastName,
        sidenavSubtitle: `${brokerDataDetails.name} ${userRoleUiMap.get(
          brokerUser.role,
        )}`,
      })),
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class AppSidenavItemsResolver implements Resolve<AppSidenavComponentData["appSidenav"]> {
  constructor(
    private brokerUserResolver: BrokerCurrentResolver,
    private readonly brokerDataResolver: BrokerDataDetailsResolver,
  ) {}

  resolve(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<AppSidenavComponentData["appSidenav"]> {
    return this.brokerUserResolver.resolve().pipe(
      mergeMap((brokerUser: BrokerUserData) =>
        forkJoin([
          of(brokerUser),
          this.brokerDataResolver.resolve()
        ]),
      ),
      map(([brokerUser, brokerDataDetails]) => {
        let appSidenavItems = baseAppSidenavItems();
        if (brokerUser.role === UserDbRole.ADMIN) {
          const teamSidenavItems = [
            {
              url: WebPaths.Team,
              label: TeamSidenavFilters.TEAM,
              icon: "people",
              subitems: [
                {
                  url: WebPaths.Team,
                  query: { roles: UserDbRole.ADMIN },
                  label: TeamSidenavFilters.ADMINS,
                },
                {
                  url: WebPaths.Team,
                  query: { roles: UserDbRole.USER },
                  label: TeamSidenavFilters.TEAM_MEMBERS,
                },
                {
                  url: WebPaths.Team,
                  query: { roles: UserDbRole.DEVELOPER },
                  label: TeamSidenavFilters.DEVELOPERS,
                },
                {
                  url: WebPaths.Team,
                  query: { active: false },
                  label: TeamSidenavFilters.DEACTIVATED_USERS,
                },
              ],
            }];

            appSidenavItems = appSidenavItems.concat(
              addDefaultAppSidenavItemOptions(teamSidenavItems, {
                query: { sort: "lastName" },
              }),
            );

            if(brokerDataDetails.apiEnabled) {
              const adminSidenavItems = [
                {
                  url: "/admin/api-keys",
                  label: AdminSidenavFilters.ADMIN,
                  icon: "manage_accounts",

                  subitems: [
                    {
                      url: "/admin/api-keys",
                      label: AdminSidenavFilters.ApiKeys,
                    },
                    {
                      url: "/admin/api-docs",
                      label: AdminSidenavFilters.ApiDocs,
                    },
                  ],
                }];

                appSidenavItems = appSidenavItems.concat(addDefaultAppSidenavItemOptions(adminSidenavItems));
            }
        }

        if(brokerUser.role === UserDbRole.DEVELOPER && brokerDataDetails.apiEnabled) {
          const adminSidenavItems = [
            {
              url: "/admin/api-docs",
              label: AdminSidenavFilters.ADMIN,
              icon: "manage_accounts",

              subitems: [
                {
                  url: "/admin/api-docs",
                  label: AdminSidenavFilters.ApiDocs,
                },
              ],
            }];

            appSidenavItems = appSidenavItems.concat(addDefaultAppSidenavItemOptions(adminSidenavItems));
        }

        return {
          // Initial paint: mark the dashboard selection from the resolved url (in-shell navigation re-marks it).
          items: markDashboardSelection(appSidenavItems, dashboardNavLabelForUrl(state.url)),
          trackByFn: (
            _index: number,
            item: AppSidenavItem | AppSidenavSubItem,
          ) : string => {
            return item.label;
          },
        };
      }),
    );
  }
}
